import React, { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import axios from "axios";
import { Loader2, Sparkles, WandSparkles, Tv } from "lucide-react";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import API_BASE from "../../config/api";
import ReferenceImageUpload from "./ReferenceImageUpload";
import SavedPrompts from "./SavedPrompts";
import GeneratedVersions from "./GeneratedVersions";
import { useWorkflowState } from "./WorkflowStateContext";
import {
  ASPECT_RATIOS,
  DEFAULT_ASPECT_RATIO,
  createPrompt,
  deletePrompt,
  downloadDataUrl,
  generateImage,
  getActiveCustomerId,
  listPrompts,
  slugify,
  updatePrompt,
} from "./imageCreationService";

// Modes ("Modes" collection / DigitalAds) are managed by Backend/controllers/modeController.js,
// not the Image Creation backend — called directly here rather than through
// imageCreationService.js's `request()` helper, since /getModes and /addPromo
// use a plain customer_id/customerId convention of their own and sit outside
// the merchantAuth-protected /image-generation routes.
const fetchModesForActiveStore = async () => {
  const customerId = getActiveCustomerId();
  if (!customerId) throw new Error("No store selected. Please sign in again.");
  const { data } = await axios.get(`${API_BASE}/getModes`, { params: { customer_id: customerId } });
  return data?.data || [];
};

const addImageToMode = async ({ modeId, title, imageData }) => {
  const customerId = getActiveCustomerId();
  if (!customerId) throw new Error("No store selected. Please sign in again.");
  const { data } = await axios.post(`${API_BASE}/addPromo`, { customerId, modeId, title, imageData });
  return data?.data;
};

const PROMPT_EXAMPLES = [
  "Change the background to a dark wooden table.",
  "Make the background brighter and more modern.",
  "Add a subtle festive border around the edges.",
];
const MAX_PROMPT = 4000;

const inputCls =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none disabled:bg-gray-50";

export default function ImageCreation({ onGenerated, onRemove, pendingItems = [], onConsumePendingItem, pendingImages = [], onConsumePendingImage }) {
  const promptRef = useRef(null);
  // Form
  const [reference, setReference] = useState(null); // template image: { file, previewUrl, name }
  const [extracted, setExtracted] = useState(null); // extracted product picture: { file, previewUrl, name }
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const [productImage, setProductImage] = useState(false); // white-background product photo mode
  const [formError, setFormError] = useState("");
  // Change Image? gate, asked AFTER a queued item's data is loaded, before any
  // generation: null (no pending decision — normal standalone use of the card),
  // "pending" (just loaded, Generate is held until answered), "no" (keep the
  // existing image, Generate stays hidden), "yes" (next Generate click redraws
  // the food photo to match the title).
  const [changeImageChoice, setChangeImageChoice] = useState(null);

  // Saved prompts
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState("");
  const [savePromptForReuse, setSavePromptForReuse] = useState(false);
  const [promptName, setPromptName] = useState("");
  const [promptBusy, setPromptBusy] = useState(false);

  // Versions — never stored server-side (the server keeps only usage
  // metadata), so they live in WorkflowStateProvider (mounted above the
  // router in App.js) instead of local state: that way the generated
  // image(s) are still here after the merchant leaves this page and comes
  // back, not just while this card stays mounted.
  const {
    imageVersions: versions,
    setImageVersions: setVersions,
    imageSelectedKey: selectedKey,
    setImageSelectedKey: setSelectedKey,
  } = useWorkflowState();
  const [generating, setGenerating] = useState(false);

  // Add to Mode — publishes the currently selected generated image straight
  // to one of the merchant's Modes (screens), right after generation.
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [modeError, setModeError] = useState("");
  const [modes, setModes] = useState([]);
  const [modesLoading, setModesLoading] = useState(false);
  const [selectedModeId, setSelectedModeId] = useState("");
  const [addingToMode, setAddingToMode] = useState(false);

  const selected = versions.find((v) => v.key === selectedKey) || null;
  const selectedPrompt = prompts.find((p) => p.id === selectedPromptId) || null;
  const promptEdited = !!selectedPrompt && selectedPrompt.prompt !== prompt.trim();

  const loadPrompts = useCallback(() => {
    listPrompts()
      .then(setPrompts)
      .catch((err) => setFormError(err.message));
  }, []);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  // Switching store changes which store these images and prompts belong to, so
  // drop the current store's work rather than letting it leak into the next one
  // (the same ACTIVE_CUSTOMER_ID_CHANGED_EVENT that Mode/Schedule Hub use).
  useEffect(() => {
    const handleStoreChanged = () => {
      setVersions([]);
      setSelectedKey("");
      setModeDialogOpen(false);
      setModeError("");
      setModes([]);
      setSelectedModeId("");
      setFormError("");
      setSelectedPromptId("");
      loadPrompts();
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleStoreChanged);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleStoreChanged);
  }, [loadPrompts]);

  // A new reference image starts a fresh set of versions.
  const handleReferenceChange = (next) => {
    setReference(next);
    setVersions([]);
    setSelectedKey("");
  };

  // The extracted picture is the second input: a new one starts a fresh set of versions.
  const handleExtractedChange = (next) => {
    setExtracted(next);
    setVersions([]);
    setSelectedKey("");
  };

  // A queued item from Menu Data Extraction loads into the form automatically
  // as soon as it arrives — no manual "Load into Form" step. It never calls
  // the generation API itself; Change Image? is asked right after, below,
  // before the merchant can generate anything with this item. Waits for any
  // in-progress generation to finish first so it never overwrites the form
  // mid-generation.
  useEffect(() => {
    if (pendingItems.length === 0 || generating) return;
    const item = pendingItems[0];

    setTitle(String(item.name || "").slice(0, 120));
    setDescription(String(item.description || "").slice(0, 500));
    setPrice(item.price != null ? String(item.price).slice(0, 40) : "");
    setChangeImageChoice("pending");
    message.info(`Loaded "${item.name || "item"}" from Menu Data Extraction.`);

    onConsumePendingItem?.();
  }, [pendingItems, generating, onConsumePendingItem]);

  // An enhanced product image sent from Image Extraction becomes the reference
  // image (and its name the title) automatically. It never calls the generation
  // API itself, and waits for any in-progress generation to finish first.
  useEffect(() => {
    if (pendingImages.length === 0 || generating) return;
    const item = pendingImages[0];
    onConsumePendingImage?.(); // consume first so a failure below can't loop
    (async () => {
      try {
        const blob = await (await fetch(item.dataUrl)).blob();
        const name = `${slugify(item.name) || "product"}.png`;
        const file = new File([blob], name, { type: blob.type || "image/png" });
        handleExtractedChange({ file, previewUrl: URL.createObjectURL(file), name });
        if (item.name) setTitle(String(item.name).slice(0, 120));
        if (item.description) setDescription(String(item.description).slice(0, 500));
        if (item.price != null && item.price !== "") setPrice(String(item.price).slice(0, 40));
        message.info(`Loaded "${item.name || "image"}" from Image Extraction as the extracted image. Add a template image to place it into.`);
      } catch {
        message.error("Couldn't load that image from Image Extraction.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingImages, generating, onConsumePendingImage]);

  const handleSelectPrompt = (id) => {
    setSelectedPromptId(id);
    const p = prompts.find((x) => x.id === id);
    if (p) setPrompt(p.prompt);
    setSavePromptForReuse(false);
  };

  const handleUpdateSavedPrompt = async () => {
    if (!selectedPrompt || !prompt.trim()) return;
    setPromptBusy(true);
    try {
      const updated = await updatePrompt(selectedPrompt.id, { prompt: prompt.trim() });
      setPrompts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      message.success(`Saved prompt "${updated.name}" updated.`);
    } catch (err) {
      message.error(err.message);
    } finally {
      setPromptBusy(false);
    }
  };

  const handleDeleteSavedPrompt = async () => {
    if (!selectedPrompt) return;
    if (!window.confirm(`Delete the saved prompt "${selectedPrompt.name}"?`)) return;
    setPromptBusy(true);
    try {
      await deletePrompt(selectedPrompt.id);
      setPrompts((prev) => prev.filter((p) => p.id !== selectedPrompt.id));
      setSelectedPromptId("");
      message.success("Saved prompt deleted.");
    } catch (err) {
      message.error(err.message);
    } finally {
      setPromptBusy(false);
    }
  };

  const runGeneration = useCallback(async () => {
    if (generating) return;
    setFormError("");
    const text = prompt.trim();
    const hasDetails = Boolean(title.trim() || description.trim() || price.trim());
    if (!reference?.file && !extracted?.file) return setFormError("Please upload a template image first.");
    if (!hasDetails && !text) {
      setFormError("Add a title, description or price, or describe the change you want.");
      return;
    }
    if (!ASPECT_RATIOS.includes(aspectRatio)) return setFormError("Please choose a valid aspect ratio.");
    if (savePromptForReuse && !promptName.trim()) return setFormError("Give the prompt a name to save it for reuse.");
    if (savePromptForReuse && !text) return setFormError("Write a prompt before saving it for reuse.");

    setGenerating(true);
    try {
      let promptId = selectedPrompt && !promptEdited ? selectedPrompt.id : undefined;
      if (savePromptForReuse) {
        try {
          const saved = await createPrompt(promptName.trim(), text);
          setPrompts((prev) => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
          setSelectedPromptId(saved.id);
          setSavePromptForReuse(false);
          setPromptName("");
          promptId = saved.id;
          message.success(`Prompt saved as "${saved.name}".`);
        } catch (err) {
          setFormError(err.message);
          return;
        }
      }

      // Template + extracted image → place the extracted picture into the template.
      // Only an extracted image (no template) → the older single-image product shot.
      const templateMode = !!(reference?.file && extracted?.file);
      const created = await generateImage({
        file: reference?.file || extracted.file,
        extractedFile: templateMode ? extracted.file : undefined,
        prompt: text,
        title: title.trim(),
        description: description.trim(),
        price: price.trim(),
        aspectRatio,
        promptId,
        // Only true when the Change Image? gate below was answered "Yes" for an
        // item loaded from Menu Data Extraction; cleared right after so it only
        // ever applies to the one generation it was meant for.
        matchImageToTitle: changeImageChoice === "yes",
        productImage: templateMode ? false : productImage || !reference?.file,
      });
      const version = { ...created, key: `${created.id || "v"}-${Date.now()}` };
      setVersions((prev) => [version, ...prev]);
      setSelectedKey(version.key);
      setChangeImageChoice(null);
      onGenerated?.(version);
    } catch (err) {
      setFormError(err.message);
      message.error(err.message);
    } finally {
      setGenerating(false);
    }
  }, [
    generating,
    prompt,
    reference,
    extracted,
    aspectRatio,
    productImage,
    savePromptForReuse,
    promptName,
    selectedPrompt,
    promptEdited,
    title,
    description,
    price,
    changeImageChoice,
    onGenerated,
  ]);

  const handleEditPrompt = () => {
    if (!selected) return;
    setPrompt(selected.prompt || "");
    setTitle(selected.title || "");
    setDescription(selected.description || "");
    setPrice(selected.price || "");
    setAspectRatio(selected.aspectRatio || DEFAULT_ASPECT_RATIO);
    if (selectedPrompt && selectedPrompt.prompt !== selected.prompt) setSelectedPromptId("");
    promptRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => promptRef.current?.focus(), 250);
  };

  const handleDownload = () => {
    if (!selected) return;
    downloadDataUrl(selected.imageDataUrl, `${slugify(selected.title) || "menu-image"}.png`);
  };

  // Discards every generated version for this card, not just the one on
  // screen — download first if you want to keep any of them.
  const handleClearVersions = () => {
    setVersions([]);
    setSelectedKey("");
    setModeDialogOpen(false);
    setModeError("");
  };

  const openModeDialog = () => {
    if (!selected) return;
    setModeDialogOpen(true);
    setModeError("");
    setSelectedModeId("");
    setModesLoading(true);
    fetchModesForActiveStore()
      .then((list) => setModes(Array.isArray(list) ? list : []))
      .catch((err) => setModeError(err.message || "Failed to load modes."))
      .finally(() => setModesLoading(false));
  };

  const closeModeDialog = () => {
    setModeDialogOpen(false);
    setModeError("");
    setSelectedModeId("");
  };

  const handleAddToMode = async () => {
    if (!selected || !selectedModeId || addingToMode) return;
    setAddingToMode(true);
    setModeError("");
    try {
      await addImageToMode({
        modeId: selectedModeId,
        title: selected.title || title.trim() || "Menu",
        imageData: selected.imageDataUrl,
      });
      message.success("Image added to Mode.");
      closeModeDialog();
    } catch (err) {
      setModeError(err.message || "Failed to add image to Mode.");
    } finally {
      setAddingToMode(false);
    }
  };

  const disabled = generating;

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
            <WandSparkles size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Image Creation</h3>
            <p className="text-xs text-gray-500">Create and edit menu images using AI</p>
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={generating}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50"
          >
            Remove from Components
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* ---------- Inputs ---------- */}
        <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:p-5 lg:border-b-0 lg:border-r">
          <ReferenceImageUpload
            value={reference}
            onChange={handleReferenceChange}
            disabled={disabled}
            label="Template Image"
            hint="The design to reuse: its layout, text and style stay the same."
            testId="template-image"
          />
          <ReferenceImageUpload
            value={extracted}
            onChange={handleExtractedChange}
            disabled={disabled}
            label="Extracted Image"
            hint="The product picture to place into the template. Comes from Image Extraction, or upload one."
            testId="extracted-image"
          />
          {extracted?.file && !reference?.file && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Add a template image to place this picture into. Without one, a plain product image is created.
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label htmlFor="ic-title" className="text-sm font-medium text-gray-900">Title</label>
              <input
                id="ic-title"
                className={inputCls}
                value={title}
                maxLength={120}
                disabled={disabled}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="ic-price" className="text-sm font-medium text-gray-900">Price</label>
              <input
                id="ic-price"
                className={inputCls}
                value={price}
                maxLength={40}
                disabled={disabled}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter price"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="ic-description" className="text-sm font-medium text-gray-900">Description</label>
            <input
              id="ic-description"
              className={inputCls}
              value={description}
              maxLength={500}
              disabled={disabled}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
            />
            <span className="text-xs text-gray-500">
              The title, price and description above are written onto the image, replacing the matching text on your template.
            </span>
          </div>

          <SavedPrompts
            prompts={prompts}
            selectedId={selectedPromptId}
            onSelect={handleSelectPrompt}
            isEdited={promptEdited}
            onUpdate={handleUpdateSavedPrompt}
            onDelete={handleDeleteSavedPrompt}
            busy={promptBusy}
            disabled={disabled}
          />

          <div className="flex flex-col gap-1.5">
            <label htmlFor="ic-prompt" className="text-sm font-medium text-gray-900">
              What would you like to change? <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="ic-prompt"
              ref={promptRef}
              rows={5}
              maxLength={MAX_PROMPT}
              value={prompt}
              disabled={disabled}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Anything beyond the item details — background, mood, decoration…"
              className={`${inputCls} resize-y leading-relaxed`}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
              <span>Leave empty to change only the title, price and description.</span>
              <span className="tabular-nums">{prompt.length}/{MAX_PROMPT}</span>
            </div>
            {!prompt && (
              <div className="flex flex-wrap gap-1.5">
                {PROMPT_EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => setPrompt(ex)}
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:border-indigo-300 hover:text-indigo-700"
                  >
                    {ex.split(".")[0]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={savePromptForReuse}
                disabled={disabled}
                onChange={(e) => setSavePromptForReuse(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600"
              />
              Save prompt for reuse
            </label>
            {savePromptForReuse && (
              <input
                aria-label="Saved prompt name"
                className={inputCls}
                value={promptName}
                maxLength={60}
                disabled={disabled}
                onChange={(e) => setPromptName(e.target.value)}
                placeholder="Name, e.g. Dark Wooden Background"
              />
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-gray-900">Aspect ratio</span>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Aspect ratio">
              {ASPECT_RATIOS.map((r) => {
                const active = aspectRatio === r;
                const [w, h] = r.split(":").map(Number);
                return (
                  <button
                    key={r}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    onClick={() => setAspectRatio(r)}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs disabled:opacity-50 ${
                      active ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-700" : "border-gray-200 text-gray-600 hover:border-gray-400"
                    }`}
                  >
                    <span
                      className="inline-block rounded-[2px] border-[1.5px] border-current"
                      style={{ width: w >= h ? 16 : Math.round((16 * w) / h), height: h >= w ? 16 : Math.round((16 * h) / w) }}
                    />
                    {r}
                    {r === DEFAULT_ASPECT_RATIO && <span className="text-[10px] opacity-70">TV</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <label className={`flex items-start gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 ${reference?.file && extracted?.file ? "hidden" : ""}`}>
            <input type="checkbox" className="mt-0.5" checked={productImage} disabled={generating} onChange={(e) => setProductImage(e.target.checked)} />
            <span>
              <span className="font-medium text-gray-900">Product image</span> — clean white background. For fruits, the image shows a whole fruit and a cut piece
              of the same fruit. The whole image is kept (never cut) and the result is exactly the chosen aspect ratio. Put the product name in the title.
            </span>
          </label>

          {formError && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}

          {/* Asked right after a Menu Data Extraction item is loaded above, and
              before any generation for it: "no" still generates, but keeps the
              depicted food photo untouched and only updates the title, price
              and description text on the menu card; "yes" also redraws the
              food photo itself around the new title. Standalone use of this
              card (no pending item) never sets this, so Generate behaves
              exactly as it always has. */}
          {changeImageChoice && (
            <div className="flex flex-col gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2.5">
              <span className="text-sm font-medium text-indigo-900">Change Image?</span>
              <div className="flex flex-col gap-1.5">
                <label className="flex items-start gap-2 text-sm text-indigo-800">
                  <input
                    type="radio"
                    name="ic-change-image"
                    className="mt-0.5"
                    checked={changeImageChoice === "no"}
                    onChange={() => setChangeImageChoice("no")}
                  />
                  <span>
                    <span className="font-medium">No</span> — keep the same photo in the menu card; only update the title, price and description.
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-indigo-800">
                  <input
                    type="radio"
                    name="ic-change-image"
                    className="mt-0.5"
                    checked={changeImageChoice === "yes"}
                    onChange={() => setChangeImageChoice("yes")}
                  />
                  <span>
                    <span className="font-medium">Yes</span> — generate a new image based on "{title || "this item"}".
                  </span>
                </label>
              </div>
              {changeImageChoice === "no" && (
                <p className="text-xs text-indigo-700">The photo stays the same — only the title, price and description will be updated.</p>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={runGeneration}
            disabled={generating || !(reference?.file || extracted?.file) || changeImageChoice === "pending"}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Creating your image..." : "Generate Image"}
          </button>
        </div>

        {/* ---------- Result ---------- */}
        <div className="p-4 sm:p-5">
          <GeneratedVersions
            versions={versions}
            selected={selected}
            onSelect={setSelectedKey}
            generating={generating}
            aspectRatio={aspectRatio}
            canRegenerate={!!(reference?.file || extracted?.file) && !!(prompt.trim() || title.trim() || description.trim() || price.trim())}
            onDownload={handleDownload}
            onRegenerate={runGeneration}
            onEditPrompt={handleEditPrompt}
            onClear={handleClearVersions}
          />

          {selected && !generating && (
            <div className="mt-3">
              {!modeDialogOpen ? (
                <button
                  type="button"
                  onClick={openModeDialog}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Tv size={14} />
                  Add to Mode
                </button>
              ) : (
                <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <span className="text-sm font-medium text-gray-900">Add to Mode</span>

                  {modesLoading ? (
                    <span className="flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 size={13} className="animate-spin" />
                      Loading your modes...
                    </span>
                  ) : modes.length === 0 ? (
                    <p className="text-xs text-gray-500">No modes found for this store.</p>
                  ) : (
                    <select
                      value={selectedModeId}
                      onChange={(e) => setSelectedModeId(e.target.value)}
                      disabled={addingToMode}
                      className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 disabled:opacity-60"
                    >
                      <option value="">Choose a mode...</option>
                      {modes.map((m) => (
                        <option key={m._id} value={m._id}>
                          {m.modeName}
                        </option>
                      ))}
                    </select>
                  )}

                  {modeError && <p className="text-xs text-red-600">{modeError}</p>}

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleAddToMode}
                      disabled={addingToMode || !selectedModeId}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {addingToMode ? <Loader2 size={13} className="animate-spin" /> : <Tv size={13} />}
                      {addingToMode ? "Adding..." : "Add to Mode"}
                    </button>
                    <button
                      type="button"
                      onClick={closeModeDialog}
                      disabled={addingToMode}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>


    </article>
  );
}
